/**
 * Exec command - fire-and-forget task dispatch
 *
 * Sends a task to the Cloudflare Sandbox Worker for headless execution.
 * The sandbox handles compute allocation, repo cloning, and coding agent CLI invocation.
 * The /api/exec endpoint blocks until done -- no polling needed.
 */

import chalk from 'chalk';
import type { TaskOptions } from '@spacecode/shared/remote';
import { validateRepo, validateBranch, validatePrompt } from '../utils/validation.js';
import { generateTaskId, truncate } from '../utils/task.js';
import { setupRun } from '../utils/run-setup.js';
import { buildRemoteTask, statusFromResult, errorTaskStatus } from '../utils/remote-task.js';

/**
 * Dispatch a task to the Cloudflare Sandbox Worker
 */
export async function execTask(options: TaskOptions): Promise<void> {
  // Validate and sanitize user inputs
  const validRepo = validateRepo(options.repo);
  const validPrompt = validatePrompt(options.prompt);
  const validBranch = options.branch ? validateBranch(options.branch) : undefined;

  const taskId = generateTaskId();

  // Set up SIGINT handler to abort the in-flight request
  const abortController = new AbortController();
  const sigintHandler = () => {
    console.log(chalk.dim('\nReceived SIGINT, aborting task...'));
    abortController.abort();
  };
  process.on('SIGINT', sigintHandler);

  // Print dispatch info
  console.log('');
  console.log(chalk.bold('Dispatching task...'));
  console.log(chalk.dim(`  Task:   ${taskId}`));
  console.log(chalk.dim(`  Repo:   ${options.repo}`));
  console.log(chalk.dim(`  Prompt: ${truncate(options.prompt, 60)}`));
  if (options.branch) {
    console.log(chalk.dim(`  Branch: ${options.branch}`));
  }
  console.log('');

  try {
    const { client, envVars } = await setupRun({ proxyMode: options.proxyMode });

    // Execute task (blocks until done -- no polling needed)
    console.log(chalk.dim('  Executing task...'));
    const result = await client.exec({
      repo: validRepo,
      prompt: validPrompt,
      envVars,
      branch: validBranch,
      noCommit: options.noCommit,
      idleTimeoutMinutes: options.idleTimeout,
    }, abortController.signal);

    // Save task record
    const { addTask } = await import('../config.js');
    const task = buildRemoteTask({
      id: taskId,
      repo: validRepo,
      prompt: validPrompt,
      mode: 'exec',
      status: statusFromResult(result.success),
      exitCode: result.exitCode,
      branch: validBranch,
    });
    await addTask(task);

    // Print result
    console.log('');
    if (result.success) {
      console.log(chalk.green('Task completed successfully!'));
    } else {
      console.log(chalk.red('Task failed.'));
    }

    console.log(chalk.bold('  Task ID: ') + chalk.cyan(taskId));

    if (result.logs) {
      console.log(chalk.dim('\nOutput:'));
      console.log(result.logs);
    }

    if (result.diff) {
      console.log(chalk.dim('\nDiff:'));
      console.log(result.diff);
    }

    if (!result.success) {
      process.exit(1);
    }
  } catch (error) {
    const isAbort = error instanceof Error && error.name === 'AbortError';

    // Save task record
    try {
      const { addTask } = await import('../config.js');
      const task = buildRemoteTask({
        id: taskId,
        repo: validRepo,
        prompt: validPrompt,
        mode: 'exec',
        status: errorTaskStatus(isAbort),
        error: isAbort ? 'Cancelled by user' : (error instanceof Error ? error.message : String(error)),
      });
      await addTask(task);
    } catch (saveErr) {
      console.warn(`Failed to save task record: ${saveErr instanceof Error ? saveErr.message : String(saveErr)}`);
    }

    if (isAbort) {
      console.log(chalk.yellow('\nTask cancelled.'));
      process.exit(130);
    } else {
      console.error(chalk.red(`\nError: ${error instanceof Error ? error.message : String(error)}`));
      process.exit(1);
    }
  } finally {
    process.removeListener('SIGINT', sigintHandler);
  }
}

// Re-export as `exec` for backward compatibility with existing callers
export { execTask as exec };
